import { useTranslation } from "react-i18next";
import {
  faBullhorn,
  faEnvelope,
  faLifeRing,
  faListCheck,
  faNoteSticky,
  faPalette,
} from "@fortawesome/free-solid-svg-icons";
import WalletBalanceBox from "components/shared/WalletBalanceBox";
import { LANGUAGE } from "services/general";
import { useTopBarCounters } from "modules/topbar/api";
import { useAmerSupportNeedsActionCount } from "modules/amer-support/api";
import { useAnnouncementsUnreadCount } from "modules/announcements/api";
import NavPillGroup from "./NavPillGroup";
import NavPillButton from "./NavPillButton";
import NavDivider from "./NavDivider";
import NotificationsMenu from "./NotificationsMenu";
import AmerHelpButton from "./AmerHelpButton";
import LanguageToggle from "./LanguageToggle";

/**
 * Desktop top-bar action rail (top-bar design): wallet pill, two icon "pill
 * clusters" (communication + personal tools), the labelled Amer Help button and
 * the language switch. The notifications bell keeps its dropdown menu.
 */
const DesktopNavIcons = ({ isDark, changeLng, handleSettings }) => {
  const { t } = useTranslation();
  const { data: counters, refetch: refetchCounters } = useTopBarCounters();
  const { data: amerSupport } = useAmerSupportNeedsActionCount({
    refetchInterval: 60000,
    staleTime: 30000,
  });
  const { data: announcements } = useAnnouncementsUnreadCount({
    refetchInterval: 300000,
    staleTime: 120000,
  });

  return (
    <div className="hidden lg:flex items-center gap-2 xl:gap-3">
      <WalletBalanceBox isDark={isDark} />

      <NavDivider isDark={isDark} />

      {/* Communication cluster */}
      <NavPillGroup isDark={isDark}>
        <NavPillButton
          isDark={isDark}
          title={t("messages.title")}
          to={`/${LANGUAGE}/support-center`}
          count={counters?.support_tickets || 0}
          icon={faLifeRing}
        />
        <NavPillButton
          isDark={isDark}
          title={t("pages.contact_messages")}
          to={`/${LANGUAGE}/contact-messages`}
          count={counters?.contact_messages || 0}
          icon={faEnvelope}
        />
      </NavPillGroup>

      {/* Personal / tools cluster */}
      <NavPillGroup isDark={isDark}>
        <NavPillButton
          isDark={isDark}
          title={t("pages.tasks")}
          to={`/${LANGUAGE}/tasks`}
          count={counters?.pending_tasks || 0}
          icon={faListCheck}
        />
        <NavPillButton
          isDark={isDark}
          title={t("pages.notes")}
          to={`/${LANGUAGE}/notes`}
          icon={faNoteSticky}
        />
        <NotificationsMenu
          isDark={isDark}
          count={counters?.notifications || 0}
          onChanged={refetchCounters}
        />
        {/* Platform news — sits beside the bell and opens the page directly. */}
        <NavPillButton
          isDark={isDark}
          title={t("announcements.title")}
          to={`/${LANGUAGE}/announcements`}
          count={announcements?.count || 0}
          icon={faBullhorn}
        />
        <NavPillButton
          isDark={isDark}
          title={t("pages.settings")}
          onClick={handleSettings}
          icon={faPalette}
        />
      </NavPillGroup>

      <NavDivider isDark={isDark} />

      <AmerHelpButton isDark={isDark} count={amerSupport?.count || 0} />

      <LanguageToggle isDark={isDark} changeLng={changeLng} />
    </div>
  );
};

export default DesktopNavIcons;
